import { apiClient } from "./apiClient";
import type { ApiResponse, PagedResult } from "./types";
import { unwrapApiResponse, unwrapPagedItems } from "../shared/utils/apiResponse";

export type PostingProfileRow = {
  id: number;
  moduleCode: string;
  transactionTypeCode: string;
  isActive: boolean;
  isCompiled?: boolean;
  compiledOn?: string | null;
  rules?: PostingRuleRow[];
};

export type PostingRuleRow = {
  key?: string;
  id?: number | null;
  postingProfileId: number;
  sequence: number;
  role: string;
  entrySide: string;
  amountSource: string;
  ledgerSource?: string;
  ledgerSourceCode?: string;
  staticLedgerId?: number | null;
  taxType?: string | null;
  taxTypeCode?: string | null;
  isTaxRule?: boolean;
  taxFieldSource?: string | null;
};

export async function listPostingProfiles(params: { pageNumber: number; pageSize: number; search?: string; isActive?: boolean }) {
  const { data } = await apiClient.post<ApiResponse<PagedResult<PostingProfileRow>>>("/posting-profiles/paged", null, { params });
  return unwrapPagedItems<PostingProfileRow>(data);
}

export async function getPostingProfile(id: number) {
  const { data } = await apiClient.get<ApiResponse<PostingProfileRow>>(`/posting-profiles/${id}`);
  return unwrapApiResponse<PostingProfileRow>(data);
}

export async function savePostingProfile(values: Record<string, unknown>, id?: number | null) {
  const payload = { isActive: true, ...values, id: id ?? values.id };
  const { data } = id
    ? await apiClient.put<ApiResponse<unknown>>(`/posting-profiles/${id}`, payload)
    : await apiClient.post<ApiResponse<number>>("/posting-profiles", payload);
  return data;
}

export async function activatePostingProfile(id: number) {
  const { data } = await apiClient.post<ApiResponse<unknown>>(`/posting-profiles/${id}/activate`);
  return data;
}

export async function deletePostingProfile(id: number) {
  const { data } = await apiClient.delete<ApiResponse<unknown>>(`/posting-profiles/${id}`);
  return data;
}

export async function listPostingRules(profileId: number) {
  const { data } = await apiClient.get<ApiResponse<PostingRuleRow[]>>("/posting-profiles/rules", { params: { profileId } });
  return unwrapApiResponse<PostingRuleRow[]>(data) ?? [];
}

export async function savePostingRules(rules: PostingRuleRow[]) {
  const payload = rules.map(({ key, ledgerSource, taxType, ...rule }) => ({
    ...rule,
    ledgerSourceCode: rule.ledgerSourceCode || ledgerSource,
    taxTypeCode: rule.taxTypeCode || taxType || null
  }));
  const { data } = await apiClient.post<ApiResponse<number>>("/posting-profiles/rules/bulk-upsert", payload);
  return data;
}
